import styles from './AdditionalInformationContent.module.css';
import Form, { Data, FormData, FormErrors } from '../../../molecules/form/Form';
import Text from '../../../atoms/typography/Text';
import { Card } from '../../../organisms/card';
import ScreeningNavigation from '../../../organisms/screening-navigation/ScreeningNavigation';
import { ColorEnum, FontSizeEnum, FontWeightEnum } from '../../../enums';
import screeningSteps, { Action } from '../../../organisms/screening-navigation/screeningSteps';
import { Trans, useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import Label from '../../../atoms/label/Label';
import FormError from '../../../molecules/form/FormError';
import { HtmlTextArea } from '../../../atoms/html-text-area';
import findAdditionalInformation, { FindAdditionalInformationResponse } from '../../../../api/screening/additional-information/findAdditionalInformation';
import Notifications from '../../../organisms/notifications/Notifications';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import SkeletonComponent from '../../../../components/SkeletonComponent';
import { FileUpload } from '../../../organisms/file-upload';
import submitAdditionalInformation, { SubmitAdditionalInformationPayload } from '../../../../api/screening/income-verification/submit-additional-information/submitAdditionalInformation';
import { useRoutePath } from '../../../utils/useRoutePath';
import DeleteFileConfirmationModal from '../../../organisms/delete-file-confirmation-modal/DeleteFileConfirmationModal';
import deleteLandlordsStatement from '../../../../api/screening/additional-information/delete-landlords-statement/deleteLandlordsStatement';
import deleteExtraDocument from '../../../../api/screening/additional-information/delete-extra-document/deleteExtraDocument';
import AdditionalInformationDocumentsEnum from '../../../enums/screening/AddititionalInformationDocumentsEnum';
import { ErrorWithData } from '../../../../utils/api';
import { NotificationTypes } from '../../../../components/notifications/BasicNotification';
import PageContentNotFound from '../../../organisms/page-content-not-found/PageContentNotFound';

const AdditionalInformationContent = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const routePath = useRoutePath();
    const { homeseekerId } = useParams();
    const [ searchParams ] = useSearchParams();

    const authorisationId = searchParams.get('authorisationId');

    const [ isFetchingAdditionalInformation, setIsFetchingAdditionalInformation ] = useState(true);
    const [ isSubmittingAdditionalInformation, setIsSubmittingAdditionalInformation ] = useState<boolean>(false);
    const [ isSubmittingAdditionalInformationSuccess, setIsSubmittingAdditionalInformationSuccess ] = useState<boolean>(false);

    const [ storedData, setStoredData ] = useState<FindAdditionalInformationResponse>({
        additionalComments: '',
        landlordsStatement: [],
        extraDocuments: [],
    });
    const [ initialFormData, setInitialFormData ] = useState<FindAdditionalInformationResponse>({
        additionalComments: '',
        landlordsStatement: [],
        extraDocuments: [],
    });
    const [ extraDocumentToDelete, setExtraDocumentToDelete ] = useState<string>(null);

    const [ documentToDelete, setDocumentToDelete ] = useState<string>(null);

    const [ formData, setFormData ] = useState<Data>({});
    const [ formErrors, setFormErrors ] = useState<FormErrors>({});

    useEffect(() => {
        fetchAdditionalInformation();
    }, []);

    const fetchAdditionalInformation = async () => {
        try {
            const response = await findAdditionalInformation(homeseekerId);

            setStoredData(response);
            setInitialFormData(response);
        } catch (error) {
            Notifications.add({
                type:  NotificationTypes.FAILED,
                message: t('api.request.failed')
            });
        } finally {
            setIsFetchingAdditionalInformation(false);
        }
    }

    const handleFormDataChange = (data: Data, _isValid: boolean, errors: FormErrors) => {
        setFormData(data);
        setFormErrors(errors);
    }

    const handleLandlordsStatementDelete = (key: string, name: string) => {
        if (storedData.landlordsStatement?.length) {
            setDocumentToDelete(AdditionalInformationDocumentsEnum.LandlordsStatement);
            DeleteFileConfirmationModal.open(key, name, homeseekerId);
        }
    }

    const handleExtraDocumentDelete = (key: string, name: string) => {
        if (storedData.extraDocuments.find(file => file.name === name)) {
            setDocumentToDelete(AdditionalInformationDocumentsEnum.Extradocuments);
            setExtraDocumentToDelete(name);
            DeleteFileConfirmationModal.open(key, name, homeseekerId);
        }
    }

    const deleteDocument = async (_key: string, filename: string, id: string) => {
        if (documentToDelete === AdditionalInformationDocumentsEnum.LandlordsStatement) {
            await deleteLandlordsStatement(filename, id);
        }
        if (documentToDelete === AdditionalInformationDocumentsEnum.Extradocuments) {
            await deleteExtraDocument(filename, id);
        }
    }

    const handleDeleteDocumentSuccess = () => {
        if (documentToDelete === AdditionalInformationDocumentsEnum.LandlordsStatement) {
            setStoredData({
                ...storedData,
                landlordsStatement: undefined
            });
        }

        if (documentToDelete === AdditionalInformationDocumentsEnum.Extradocuments) {
            const extraDocumentKeyToDelete = extraDocumentToDelete;
            setExtraDocumentToDelete(null);

            setStoredData({
                ...storedData,
                extraDocuments: storedData.extraDocuments.filter(file => file.name !== extraDocumentKeyToDelete)
            });
        }
    }

    const handleSubmit = async () => {
        setIsSubmittingAdditionalInformation(true);
        setIsSubmittingAdditionalInformationSuccess(false);

        const startTime = Date.now();

        try {
            const payload: SubmitAdditionalInformationPayload = {
                additionalComments: formData['additionalComments']?.value as string,
                landlordsStatement: formData['landlordsStatement']?.value as FileList,
                extraDocuments: formData['extraDocuments']?.value as FileList,
            };

            await submitAdditionalInformation(homeseekerId, payload);

            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(300 - elapsedTime, 0);

            await new Promise(resolve => setTimeout(resolve, remainingTime));

            setIsSubmittingAdditionalInformation(false);
            setIsSubmittingAdditionalInformationSuccess(true);

            await new Promise(resolve => setTimeout(resolve, 300 + 150));

            navigate(routePath('ScreeningOverview', { homeseekerId }, { authorisationId }));

        } catch (error) {
            if ('data' in error && error.data.length > 0) {
                const errorData: ErrorWithData['data'] = error.data;
                errorData.forEach((errorItem) => {
                    Notifications.add({
                        type:  NotificationTypes.FAILED,
                        message: (
                            <Trans
                                i18nKey={errorItem.translationKey}
                                values={errorItem.params}
                                components={{ strong: <strong /> }}
                                ns="errors"
                            />
                        ),
                    });
                });
            } else {
                Notifications.add({
                    type:  NotificationTypes.FAILED,
                    message: t('api.request.failed'),
                });
            }
        } finally {
            setIsSubmittingAdditionalInformation(false);
            setIsSubmittingAdditionalInformationSuccess(false);
        }
    };
    const handleNavigationClick = (action: Action) => {
        if (action === 'previous') {
            navigate(routePath('ScreeningIncomeVerificationOverview', { homeseekerId }, { authorisationId }));
        }
    }

    if (!authorisationId) {
        return <PageContentNotFound/>;
    }

    if (isFetchingAdditionalInformation) {
        return (
            <div className={styles.contentContainer}>
                <div className={styles.gridLg}>
                    <SkeletonComponent height={36} width={400} />
                    <SkeletonComponent height={200} />
                    <div className={styles.skeletonButtonContainer}>
                        <SkeletonComponent height={42} width={200} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <Form
                onChange={handleFormDataChange}
                onSubmit={handleSubmit}
                initialFormData={initialFormData as unknown as FormData}
            >
                <div className={styles.contentContainer}>
                    <div className={styles.gridLg}>
                        <Text
                            color={ColorEnum.Primary700}
                            weight={FontWeightEnum.Bold}
                            size={FontSizeEnum.DisplayXs}
                        >{t(screeningSteps.ScreeningAdditionalInformation.pageTitleTranslationKey)}</Text>
                        <Card elevated>
                            <div className={styles.gridMd}>
                                <Text
                                    color={ColorEnum.Gray800}
                                    size={FontSizeEnum.Md}
                                >{t('screening.additionalInformation.description')}</Text>
                                <div>
                                    <Label
                                        htmlFor={'additionalComments'}
                                        fontSize={FontSizeEnum.Sm}
                                    >
                                        {t('screening.additionalInformation.additionalComments.label')}
                                    </Label>
                                    <HtmlTextArea
                                        id='additionalComments'
                                        name='additionalComments'
                                        placeholder={t('screening.additionalInformation.additionalComments.placeholder')}
                                        value={formData['additionalComments']?.value as string}
                                        maxlength={1000}
                                    />
                                    <FormError errors={formErrors['additionalComments']}/>
                                </div>
                                <div>
                                    <Label
                                        htmlFor={AdditionalInformationDocumentsEnum.LandlordsStatement}
                                        fontSize={FontSizeEnum.Sm}
                                    >
                                        {t('screening.additionalInformation.landlordsStatement.label')}
                                    </Label>
                                    <FileUpload
                                        name={AdditionalInformationDocumentsEnum.LandlordsStatement}
                                        storedFileMetadata={storedData?.landlordsStatement}
                                        acceptedExtensions={['pdf']}
                                        errors={formErrors[AdditionalInformationDocumentsEnum.LandlordsStatement]}
                                        onDelete={(name: string) => handleLandlordsStatementDelete(AdditionalInformationDocumentsEnum.LandlordsStatement, name)}
                                    ></FileUpload>
                                </div>
                                <div>
                                    <Label
                                        htmlFor={AdditionalInformationDocumentsEnum.Extradocuments}
                                        fontSize={FontSizeEnum.Sm}
                                    >
                                        {t('screening.additionalInformation.extraDocuments.label')}
                                    </Label>
                                    <FileUpload
                                        multiple
                                        name={AdditionalInformationDocumentsEnum.Extradocuments}
                                        storedFileMetadata={storedData?.extraDocuments}
                                        acceptedExtensions={['pdf']}
                                        errors={formErrors[AdditionalInformationDocumentsEnum.Extradocuments]}
                                        onDelete={(name: string) => handleExtraDocumentDelete(AdditionalInformationDocumentsEnum.Extradocuments, name)}
                                    ></FileUpload>
                                </div>
                            </div>
                        </Card>
                        <ScreeningNavigation
                            onClick={handleNavigationClick}
                            isSubmitting={isSubmittingAdditionalInformation}
                            isSuccessful={isSubmittingAdditionalInformationSuccess}
                        />
                    </div>
                </div>
            </Form>
            <DeleteFileConfirmationModal
                deleteCallback={deleteDocument}
                onDeleteSuccess={handleDeleteDocumentSuccess}
            ></DeleteFileConfirmationModal>
        </>
    );
}

export default AdditionalInformationContent;
